import type { MusicTheme } from '../types';

const SONGS: Record<MusicTheme, number[]> = {
  secession: [64, 67, 71, 69, 67, 64, 62, 59, 64, 67, 74, 71, 69, 67, 64, 62],
  industrial: [48, 55, 51, 58, 48, 55, 60, 58, 51, 58, 55, 62, 51, 58, 63, 62],
  mountain: [57, 64, 67, 69, 67, 64, 60, 62, 57, 64, 69, 72, 69, 67, 64, 60],
};

export class AudioManager {
  private context?: AudioContext;
  private musicStep = 0;
  private lastMusicAt = 0;
  muted = false;

  resume(): void {
    if (!this.context) {
      const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (Context) this.context = new Context();
    }
    void this.context?.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  updateMusic(now: number, theme: MusicTheme): void {
    if (this.muted || !this.context || now - this.lastMusicAt < 260) return;
    this.lastMusicAt = now;
    const notes = SONGS[theme];
    const note = notes[this.musicStep % notes.length];
    const accent = this.musicStep % 4 === 0;
    this.tone(midi(note), 0.13, accent ? 'square' : 'triangle', accent ? 0.028 : 0.018);
    if (accent) this.tone(midi(note - 24), 0.09, 'square', 0.014);
    this.musicStep += 1;
  }

  bubble(): void {
    this.tone(520, 0.07, 'sine', 0.055, 770);
  }

  pop(): void {
    this.tone(850, 0.08, 'sine', 0.055, 1_150);
    window.setTimeout(() => this.tone(1_020, 0.05, 'triangle', 0.035), 30);
  }

  jump(): void {
    this.tone(280, 0.06, 'square', 0.025, 410);
  }

  hurt(): void {
    this.tone(150, 0.14, 'sawtooth', 0.045, 85);
  }

  rescue(): void {
    [523, 659, 784].forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, 0.11, 'triangle', 0.045), index * 90);
    });
  }

  victory(): void {
    [523, 659, 784, 1_047].forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, 0.16, 'square', 0.04), index * 100);
    });
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency = frequency,
  ): void {
    if (this.muted || !this.context) return;
    const start = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), start + duration);
    gain.gain.setValueAtTime(volume, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(this.context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration);
  }
}

function midi(note: number): number {
  return 440 * 2 ** ((note - 69) / 12);
}

export const audio = new AudioManager();
